import { useEffect, useState } from 'react';
import { api, type CreditBalance } from './api';

// ---------------------------------------------------------------------------
// The balance, everywhere at once (204).
//
// Every spending call ends by telling this target that something was spent,
// and a 402 tells it the workspace came up short. The pill in the top bar
// and the Credits page listen, so a press on any bench moves the number the
// creator can see without each bench knowing the pill exists. An
// EventTarget rather than a store: there is one number and two listeners.
// ---------------------------------------------------------------------------

export { creditEvents, announceSpent, announceShort, type Short } from './creditEvents';
import { creditEvents } from './creditEvents';

/** The workspace's balance, refreshed after every press and every refusal. */
export function useCreditBalance(): { credits: CreditBalance | null; reload: () => void } {
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    api.credits().then((c) => { if (live) setCredits(c); }).catch(() => undefined);
    const bump = () => setRound((n) => n + 1);
    creditEvents.addEventListener('spent', bump);
    creditEvents.addEventListener('short', bump);
    return () => { live = false; creditEvents.removeEventListener('spent', bump); creditEvents.removeEventListener('short', bump); };
  }, [round]);
  return { credits, reload: () => setRound((n) => n + 1) };
}

/** Low under fifteen percent of the month's grant — the same line the allowances use (Billing.tsx). */
export function balanceState(c: CreditBalance | null): 'fine' | 'low' | 'spent' | 'unmetered' {
  if (!c) return 'fine';
  if (c.unmetered) return 'unmetered';
  if (c.balance.total <= 0) return 'spent';
  const grant = c.plan.monthlyCredits ?? 0;
  return grant > 0 && c.balance.total < grant * 0.15 ? 'low' : 'fine';
}
